<template>
  <div>
    <!-- Affichage d'une barre de progression pendant le chargement des données -->
    <load-progress :condition="!dataLoaded" label="Chargement des données"></load-progress>
    <!-- Affichage du tree view et des éditeurs selon l'objet sélectionné -->

    <div v-if="dataLoaded">
        <fiche-container ref="FICHE_CONTAINER"
                         :fiche="selectedFiche"
                         :route="notifRoute"
                         :attribut="attribut"
                         :route-element="routeElement"
                         @back="back"
                         @selectItem="selectItem">
        </fiche-container>
    </div>
    <div v-for="(button, index) in extraButtons" :key="index">
      <loader-button class="ms-2"
                     :outlined="button.outlined"
                     @callback="button.callback()"
                     :label="button.label"
                     :disabled="button.disabled">
      </loader-button>
    </div>
    <section-container :dialogOpen="showWizard"
                       :section="sectionWizard"
                       @closeDialog="hideWizard">
    </section-container>
  </div>
</template>

<script lang="ts">
  import { defineComponent, markRaw, type PropType } from "vue";
  import LoadProgress from '@/framework/base/components/LoadProgress.vue';
  import {
    storeSession,
    storeUser,
    storeChat,
    storeContrat,
    storeDocument,
    storeNotif,
    storePaie,
    storeSalarie
  } from '@/generated/stores';
  import { CiRouter } from '@/framework/base/helpers/NavHelper';
  import Route from '@/generated/classes/Route';
  import ModuleBase from '@/framework/base/components/ModuleBase';
  import FicheDocumentListExtend from '@/extend/fiches/FicheDocumentListExtend.vue';
  import SectionDocumentListExtend from '@/extend/sections/SectionDocumentListExtend.vue';
  import FicheDocumentViewerExtend from '@/extend/fiches/FicheDocumentViewerExtend.vue';
  import SectionDocumentViewer from '@/custom/sections/SectionDocumentViewer.vue';
  import FicheContratViewerExtend from '@/extend/fiches/FicheContratViewerExtend.vue';
  import SectionContratViewer from '@/custom/sections/SectionContratViewer.vue';
  import FichePaieViewerExtend from '@/extend/fiches/FichePaieViewerExtend.vue';
  import SectionPaieViewer from '@/custom/sections/SectionPaieViewer.vue';
  import { Fiches, Modules, Sections } from '@/generated/classes/AppSchema';
  import { GenModule, RessourceGenModule } from '@/generated/ressources/generatedRessources';
  import Wizard from '@/extend/classes/Wizard';
  import Section from '@/framework/base/classes/Section';
  import SectionWizard from '@/framework/wizard/components/SectionWizard.vue';
  import Fiche from '@/framework/base/classes/Fiche';
  import { RouteElements } from '@/framework/base/classes/Section';
  import User from '@/generated/classes/User';
  import FicheContainerAbstract from "@/framework/base/components/FicheContainerAbstract.vue";

  export function ModuleDocumentOpenFiche(route: Route | undefined): RouteElements {
    const section = new Section();
    const fiche = new Fiche();
    if (route && route.fiche) {
      switch (route.fiche) {
        case Fiches.DOCUMENT_LIST: {
          fiche.component = FicheDocumentListExtend;
          fiche.type = 'document'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeDocument.createNewDocument();
            } else {
              fiche.data = storeDocument.getDocument(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.DOCUMENT_VIEWER: {
          fiche.component = FicheDocumentViewerExtend;
          fiche.type = 'document'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeDocument.createNewDocument();
            } else {
              fiche.data = storeDocument.getDocument(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.CONTRAT_VIEWER: {
          fiche.component = FicheContratViewerExtend;
          fiche.type = 'contrat'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeContrat.createNewContrat();
            } else {
              fiche.data = storeContrat.getContrat(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.PAIE_VIEWER: {
          fiche.component = FichePaieViewerExtend;
          fiche.type = 'paie'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storePaie.createNewPaie();
            } else {
              fiche.data = storePaie.getPaie(route.ficheObjId);
            }
          }
          break;
        }
        case Fiches.CONTAINER: {
          fiche.component = FicheContainerAbstract;
          fiche.type = 'document'
          if (route && route.ficheObjId != null) {
            if (route.ficheObjId == 0) {
              fiche.data = storeDocument.createNewDocument();
            } else {
              fiche.data = storeDocument.getDocument(route.ficheObjId);
            }
          }
          break;
        }
      }
    } else {
      fiche.component = FicheDocumentListExtend;
      fiche.type = 'document'
      if (route && route.ficheObjId != null) {
        if (route.ficheObjId == 0) {
          fiche.data = storeDocument.createNewDocument();
        } else {
          fiche.data = storeDocument.getDocument(route.ficheObjId);
        }
      }
    }
    if (route && route.section && route.section != Sections.NONE) {
      switch (route.section) {
        case Sections.DOCUMENT_LIST: {
          section.component = SectionDocumentListExtend;
          section.type = 'document'
          if (route.sectionObjId == 0) {
            section.data = storeDocument.createNewDocument();
          } else {
            section.data = storeDocument.getDocument(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.DOCUMENT_VIEWER: {
          section.component = SectionDocumentViewer;
          section.type = 'document'
          if (route.sectionObjId == 0) {
            section.data = storeDocument.createNewDocument();
          } else {
            section.data = storeDocument.getDocument(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.CONTRAT_VIEWER: {
          section.component = SectionContratViewer;
          section.type = 'contrat'
          if (route.sectionObjId == 0) {
            section.data = storeContrat.createNewContrat();
          } else {
            section.data = storeContrat.getContrat(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
        case Sections.PAIE_VIEWER: {
          section.component = SectionPaieViewer;
          section.type = 'paie'
          if (route.sectionObjId == 0) {
            section.data = storePaie.createNewPaie();
          } else {
            section.data = storePaie.getPaie(route.sectionObjId);
          }
          section.showButton = true;
          break;
        }
      }
    }
    if (fiche && fiche.component) {
      fiche.component = markRaw(fiche.component);
    }
    if (section && section.component) {
      section.component = markRaw(section.component);
    }
    const res = new RouteElements();
    res.fiche = fiche;
    res.section = section;
    return res;
  }

  export default defineComponent({
    extends: ModuleBase,
    components: {
      LoadProgress,
    },

    props: {
    },

    created (): void {
      if (this.route) {
        this.notifRoute = this.route;
      }
      const session = storeSession.connectedUser;
      if (session) {
        const user = storeUser.getUser(session.id);
        if (user.modules.includes(Modules.DOCUMENT)) {
          this.wizardDone = true;
        }
      }
      if (!this.wizardDone) {
        Wizard.showWizard(Modules.DOCUMENT).then((res) => {
          if (res.id > 0) {
            this.wizard = res;
            this.showWizard = true;
          } else {
            User.acceptCgu(true);
            this.wizardDone = true;
          }
        })
      }
      this.loadStores();
    },

    data() {
      const attribut: unknown = undefined;
      const route = CiRouter.getRoute();
      return {
        wizard: new Wizard(),
        attribut,
        notifRoute: new Route(),
        route,
      }
    },

    computed: {
      ressource () {
        return RessourceGenModule(GenModule.MODULE_DOCUMENT);
      },
      sectionWizard (): Section {
        const section = new Section();
        section.component = SectionWizard;
        section.data = this.wizard;
        section.helper = true;
        return section;
      },

      storesLoaded (): boolean {
        return true;
      },

      dataLoaded (): boolean {
        const res = this.wizardDone && this.storesLoaded
        if (res && this.init) {
          this.setInit(false);
          if (!this.route) {
            this.selectItem(this.route);
          }
          CiRouter.setRouteChangedSelector(this.routeChanged);
        }
        return res;
      }
    },

    methods: {
      loadStores() : void{
        // Load and register events on used server classes
        storeChat.registerChat();
        storeContrat.registerContrat();
        storeDocument.registerDocument();
        storeNotif.registerNotif();
        storePaie.registerPaie();
        storeSalarie.registerSalarie();
      },
      setInit(value: boolean) : void{
        this.init = value;
      },
      routeChanged (route: Route): void {
        this.selectItem(route);
      },
      selectItem(route: Route | undefined): void {
        const routeElement = ModuleDocumentOpenFiche(route);
        this.routeElement = routeElement;
        this.selectedFiche = routeElement.fiche;
        this.selectedFiche.ressourceHeader = this.ressource;
        if (route) {
          this.notifRoute = route;
          if (route && route.attribut) {
            this.attribut = route.attribut;
          } else {
            this.attribut = undefined;
          }
          if (route.section && route.section != Sections.NONE) {
            this.lastShowSection = !this.lastShowSection;
            this.selectedFiche.showSection = this.lastShowSection;
          }
        }
        const index = this.lastSelectedFiche.findIndex(item => {
          return item.component === this.selectedFiche.component;
        })
        if (index >= 0) {
          this.lastRoutes.splice(index + 1)
          this.lastSelectedFiche.splice(index + 1)
        } else {
          this.lastRoutes.push(route);
          this.lastSelectedFiche.push(this.selectedFiche);
        }
      }
    }
  })
</script>
